import axiosInstance from '@/apis/axiosInstance';
import { formatPriceDollar } from '@/biz/order';
import { getPlatformVideoContentType } from '@/biz/platform';
import { isFromApp } from '@/biz/tool';
import {
  CheckLinearIcon,
  LightIcon,
  RequiredIcon
} from '@/components/common/icon';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { usePlatformStore } from '@/hooks/usePlatformStore';
import { BasePriceKeyListMap } from '@/interface/common';
import { Alert, Button, toast } from 'aha-components';
import { ReactNode, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { getEstimatedRateLevel } from './rateValidation';

const WarningAlert = ({
  title,
  description,
  setAsBaseRate,
  setBaseRateLoading,
  setBaseRateSuccess,
  newOrderPrice,
  showBaseRateAction = true,
  variant = 'warning'
}: {
  title: string;
  description: ReactNode;
  setAsBaseRate: Function;
  setBaseRateLoading: boolean;
  setBaseRateSuccess: boolean;
  newOrderPrice: number | string;
  showBaseRateAction?: boolean;
  variant?: 'warning' | 'error';
}) => {
  const { t } = useTranslation();

  // 设置输入的价格为平台底价
  const setNewOrderPriceToPlatformBasePrice = () => {
    return (
      <div className="flex flex-col md:flex-row justify-between items-end md:items-center gap-4 pl-[28px]">
        <div className="test-[#57534D] text-sm font-normal leading-5">
          <div className="test-[#57534D] text-sm font-normal leading-5">
            {t('price.fields.set_your_rate.set_base_price_title', {
              defaultValue:
                'Stick with this rate? Set it as your base rate so future deals match your price range.'
            })}
          </div>
        </div>
        <div>
          <Button
            type="default"
            size="small"
            loading={setBaseRateLoading}
            onClick={() => {
              setAsBaseRate();
            }}
          >
            <div
              className={`flex items-center gap-2 whitespace-nowrap ${setBaseRateSuccess && !setBaseRateLoading ? 'text-[#079455]' : ''}`}
            >
              {setBaseRateSuccess && !setBaseRateLoading ? (
                <CheckLinearIcon className="w-5 h-5" />
              ) : null}
              {setBaseRateSuccess && !setBaseRateLoading
                ? t('price.fields.set_your_rate.set_base_price_btn_2', {
                    defaultValue: 'Base rate set to {{price}}',
                    price: formatPriceDollar(newOrderPrice)
                  })
                : t('price.fields.set_your_rate.set_base_price_btn', {
                    defaultValue: 'Set as base rate'
                  })}
            </div>
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`${variant === 'error' ? 'bg-[#FEF3F2]' : 'bg-[#FFFAEB]'} px-4 py-3 flex flex-col gap-3 rounded-[8px]`}
    >
      <div className="flex gap-2">
        {variant === 'error' ? (
          <span className="mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[5px] bg-[#F04438] text-xs font-bold leading-none text-white">
            ×
          </span>
        ) : (
          <LightIcon className="flex-shrink-0" />
        )}
        <div className="flex flex-col gap-1">
          <div className="justify-start text-[#1C1917] text-base font-semibold leading-6">
            {title}
          </div>
          <div className="justify-center text-[#57534D]">{description}</div>
        </div>
      </div>
      {showBaseRateAction && (
        <>
          <div className="border-t border-[#E7E5E4]"></div>
          {setNewOrderPriceToPlatformBasePrice()}
        </>
      )}
    </div>
  );
};

export default function EstimateFairTips({
  low,
  high,
  creatorMaxActualGet,
  newOrderPrice,
  setBaseRateSuccess,
  setSetBaseRateSuccess
}: {
  low: number;
  high: number;
  creatorMaxActualGet: number | null;
  newOrderPrice: number | string;
  setBaseRateSuccess: boolean;
  setSetBaseRateSuccess: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const { order } = detailData;
  const { user, setUser } = usePlatformStore();
  const { selectedPriceFields } = useMultiplePricesStore();
  const interestedPriceFields = order?.interestedPriceFields;

  // 一处样式代码，用于展示基础价格设置成功后的提示
  const renderBenefitsUnlocked = () => {
    const currentContentType =
      selectedPriceFields?.[0]?.contentType ||
      interestedPriceFields?.[0]?.contentType;
    const baseKey = BasePriceKeyListMap[order?.platform]?.[currentContentType];
    const savedBaseRate = baseKey
      ? Number(user?.baselineConfig?.[baseKey]) || 0
      : 0;
    const inputPrice = Number(newOrderPrice) || 0;

    if (!savedBaseRate || inputPrice !== savedBaseRate) return null;

    return (
      <div
        className={`bg-gradient-to-r from-[#FFEAD5] to-[#fff] rounded-[8px] p-3 flex items-center justify-between`}
      >
        <div className="flex flex-col justify-center items-start gap-2">
          <div
            className={`text-[#79716B] text-xs font-semibold uppercase leading-4 tracking-[4.80px]`}
          >
            {t('onboarding.base_rate.unlock_benefits', {
              defaultValue: 'Benefits unlocked'
            })}
          </div>
          <div className="flex flex-col justify-start items-start gap-2">
            <div className="inline-flex justify-start items-start gap-2">
              <RequiredIcon className="text-[#FB6011]" />
              <div className="justify-start text-[#44403C] text-sm font-medium leading-5">
                {t('price.fields.set_your_rate.unlock_benefits_1', {
                  defaultValue:
                    'Spend less time negotiating, more time creating'
                })}
              </div>
            </div>
            <div className="inline-flex justify-start items-start gap-2">
              <RequiredIcon className="text-[#FB6011]" />
              <div className="justify-start text-[#44403C] text-sm font-medium leading-5">
                {t('price.fields.set_your_rate.unlock_benefits_2', {
                  defaultValue:
                    'Priority visibility in recommendations to brands'
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const [setBaseRateLoading, setSetBaseRateLoading] = useState(false);

  const setAsBaseRate = async () => {
    setSetBaseRateLoading(true);
    const currentPlatform = order?.platform;
    const currentContentType =
      selectedPriceFields?.[0]?.contentType ||
      interestedPriceFields?.[0]?.contentType ||
      getPlatformVideoContentType({
        platform: order?.platform
      });
    const currentInputPrice = Number(newOrderPrice || 0);

    const previousBaselineData =
      user?.baselineConfig && typeof user.baselineConfig === 'object'
        ? { ...user.baselineConfig }
        : {};
    const baselineKey =
      BasePriceKeyListMap[currentPlatform]?.[currentContentType];
    const newBaselineData = {
      ...previousBaselineData,
      [baselineKey as string]: currentInputPrice
    };
    try {
      const { data } = await axiosInstance.post(
        '/user/baselineConfig',
        newBaselineData
      );
      if (data.code === 0) {
        toast({
          message: t('price.fields.set_your_rate.set_base_price_success', {
            defaultValue: 'Set as base rate successfully!'
          }),
          position: 'top-right',
          duration: 2000,
          showClose: false,
          type: 'success'
        });
        const { data: infoData } = await axiosInstance.get(
          `/influencer/info?from=${isFromApp() ? 'app' : 'web'}`
        );
        const latestUser = infoData?.data?.user;
        const latestBaselineConfig = infoData?.data?.user?.baselineConfig;
        if (latestUser && typeof latestUser === 'object') {
          setUser(latestUser);
        } else if (
          latestBaselineConfig &&
          typeof latestBaselineConfig === 'object'
        ) {
          setUser({
            ...user,
            baselineConfig: latestBaselineConfig
          });
        }
        if (!setBaseRateSuccess) {
          setSetBaseRateSuccess(true);
        }
      }
    } catch (error) {
      toast({
        message: t('price.fields.set_your_rate.set_base_price_error', {
          defaultValue: 'Set as base rate failed!'
        }),
        position: 'top-right',
        duration: 2000,
        showClose: false,
        type: 'error'
      });
      console.error('setAsBaseRate failed:', error);
    } finally {
      setSetBaseRateLoading(false);
    }
  };

  const getTips = () => {
    const estimatedRateLevel = getEstimatedRateLevel(newOrderPrice, low, high);

    if (estimatedRateLevel === 'error') {
      return (
        <WarningAlert
          variant="error"
          title={t(
            'price.fields.set_your_rate.fair_tips.excessive_rate_title',
            {
              defaultValue: 'Lower your rate to continue'
            }
          )}
          description={
            <Trans
              i18nKey="price.fields.set_your_rate.fair_tips.excessive_rate_description"
              defaults="We want to help you land this collaboration. <strong>This rate is too far above the recommended range and can’t be submitted.</strong> At this price, most advertisers are likely to pass before starting a conversation. Lower your rate to attract more interest and move the deal forward."
              components={{
                strong: <strong className="font-semibold" />
              }}
            />
          }
          setAsBaseRate={() => setAsBaseRate()}
          setBaseRateLoading={setBaseRateLoading}
          setBaseRateSuccess={setBaseRateSuccess}
          newOrderPrice={newOrderPrice}
          showBaseRateAction={false}
        />
      );
    }

    if (estimatedRateLevel === 'warning') {
      return (
        <WarningAlert
          title={t('price.fields.set_your_rate.fair_tips.high_rate_title', {
            defaultValue: 'Boost your chances of landing this deal'
          })}
          description={t(
            'price.fields.set_your_rate.fair_tips.high_rate_description',
            {
              defaultValue:
                'At this rate, only around 20–30% of advertisers are likely to show interest in your offer. Bringing your rate closer to the recommended range can attract more interest, start more conversations, and improve your chances of landing the deal.'
            }
          )}
          setAsBaseRate={() => setAsBaseRate()}
          setBaseRateLoading={setBaseRateLoading}
          setBaseRateSuccess={setBaseRateSuccess}
          newOrderPrice={newOrderPrice}
          showBaseRateAction={false}
        />
      );
    }

    if (creatorMaxActualGet == null) return null;

    const formattedBudget = formatPriceDollar(creatorMaxActualGet);

    // 场景1: 达人可报最高价格 max < fair_low（广告主预算低于整个公允区间）
    if (creatorMaxActualGet < low) {
      // 达人报价低于可报最高价格
      if (+newOrderPrice <= creatorMaxActualGet) {
        return (
          <Alert
            type="success"
            title={t(
              'price.fields.set_your_rate.fair_tips.strong_chance_title',
              {
                defaultValue: 'Strong chance of landing this'
              }
            )}
            description={t(
              'price.fields.set_your_rate.fair_tips.below_fair_range_confirm',
              {
                defaultValue:
                  "You're well placed to land this one. Note that your quote is below the fair price range for this deal, so you may want to take that into account before confirming."
              }
            )}
          />
        );
      }
      // 达人报价高于可报最高价格 && 小于 low
      if (+newOrderPrice > creatorMaxActualGet && +newOrderPrice < low) {
        return (
          <WarningAlert
            title={t(
              'price.fields.set_your_rate.fair_tips.little_chance_title',
              {
                defaultValue: 'Little chance at this price'
              }
            )}
            description={t(
              'price.fields.set_your_rate.fair_tips.below_fair_above_budget_s1',
              {
                defaultValue:
                  "Although your quote is below the fair price range, it's already above this brand's budget of {{budget}}, so it may be harder to land. Quoting {{budget}} or lower gives you the best chance.",
                budget: formattedBudget
              }
            )}
            setAsBaseRate={() => setAsBaseRate()}
            setBaseRateLoading={setBaseRateLoading}
            setBaseRateSuccess={setBaseRateSuccess}
            newOrderPrice={newOrderPrice as number | string}
          />
        );
      }
      // 达人报价大于等于low && 小于等于high
      if (+newOrderPrice >= low && +newOrderPrice <= high) {
        return (
          <WarningAlert
            title={t(
              'price.fields.set_your_rate.fair_tips.little_chance_title',
              {
                defaultValue: 'Little chance at this price'
              }
            )}
            description={t(
              'price.fields.set_your_rate.fair_tips.within_fair_budget_below_market_s1',
              {
                defaultValue:
                  "Your quote sits within the fair price range, so your rate is fair. This brand's budget ({{budget}}) is below market for this deal, so it may be tougher to land here.",
                budget: formattedBudget
              }
            )}
            setAsBaseRate={() => setAsBaseRate()}
            setBaseRateLoading={setBaseRateLoading}
            setBaseRateSuccess={setBaseRateSuccess}
            newOrderPrice={newOrderPrice as number | string}
          />
        );
      }
      // 达人报价大于high
      if (+newOrderPrice > high) {
        return (
          <WarningAlert
            title={t(
              'price.fields.set_your_rate.fair_tips.little_chance_title',
              {
                defaultValue: 'Little chance at this price'
              }
            )}
            description={t(
              'price.fields.set_your_rate.fair_tips.above_fair_and_budget_s1',
              {
                defaultValue:
                  "Your quote is above both the fair price range and this brand's budget ({{budget}}), so it could be quite hard to land.",
                budget: formattedBudget
              }
            )}
            setAsBaseRate={() => setAsBaseRate()}
            setBaseRateLoading={setBaseRateLoading}
            setBaseRateSuccess={setBaseRateSuccess}
            newOrderPrice={newOrderPrice as number | string}
          />
        );
      }
      return null;
    }
    // 场景2:  fair_low <= 达人可报最高价格 < fair_high
    if (creatorMaxActualGet >= low && creatorMaxActualGet <= high) {
      // 达人报价小于等于low
      if (+newOrderPrice <= low) {
        return (
          <Alert
            type="success"
            title={t(
              'price.fields.set_your_rate.fair_tips.strong_chance_title',
              {
                defaultValue: 'Strong chance of landing this'
              }
            )}
            description={t(
              'price.fields.set_your_rate.fair_tips.below_fair_range_confirm',
              {
                defaultValue:
                  "You're well placed to land this one. Note that your quote is below the fair price range for this deal, so you may want to take that into account before confirming."
              }
            )}
          />
        );
      }
      // 达人报价大于low && 小于等于max
      if (+newOrderPrice > low && +newOrderPrice <= creatorMaxActualGet) {
        return (
          <Alert
            type="success"
            title={t(
              'price.fields.set_your_rate.fair_tips.strong_chance_title',
              {
                defaultValue: 'Strong chance of landing this'
              }
            )}
            description={t(
              'price.fields.set_your_rate.fair_tips.within_fair_inside_budget_s2',
              {
                defaultValue:
                  "You're within the fair price range and inside budget, fair for both sides. A great chance of landing this."
              }
            )}
          />
        );
      }
      // 达人报价大于max && 小于等于high
      if (+newOrderPrice > creatorMaxActualGet && +newOrderPrice <= high) {
        return (
          <WarningAlert
            title={t(
              'price.fields.set_your_rate.fair_tips.little_chance_title',
              {
                defaultValue: 'Little chance at this price'
              }
            )}
            description={t(
              'price.fields.set_your_rate.fair_tips.within_fair_above_budget_s2',
              {
                defaultValue:
                  "Although your quote is within the fair price range, it's above this brand's budget of {{budget}}, so it may be harder to land. Lowering to {{budget}} keeps you fair and gives you the best chance.",
                budget: formattedBudget
              }
            )}
            setAsBaseRate={() => setAsBaseRate()}
            setBaseRateLoading={setBaseRateLoading}
            setBaseRateSuccess={setBaseRateSuccess}
            newOrderPrice={newOrderPrice as number | string}
          />
        );
      }
      // 达人报价大于high
      if (+newOrderPrice > high) {
        return (
          <WarningAlert
            title={t(
              'price.fields.set_your_rate.fair_tips.little_chance_title',
              {
                defaultValue: 'Little chance at this price'
              }
            )}
            description={t(
              'price.fields.set_your_rate.fair_tips.above_fair_and_budget_s2',
              {
                defaultValue:
                  "Your quote is above both the fair price range and the brand's budget ({{budget}}), so it could be quite hard to land.",
                budget: formattedBudget
              }
            )}
            setAsBaseRate={() => setAsBaseRate()}
            setBaseRateLoading={setBaseRateLoading}
            setBaseRateSuccess={setBaseRateSuccess}
            newOrderPrice={newOrderPrice as number | string}
          />
        );
      }

      return null;
    }

    // 场景3:  达人可报最高价格 > fair_high
    if (creatorMaxActualGet > high) {
      // 达人报价小于low
      if (+newOrderPrice < low) {
        return (
          <Alert
            type="success"
            title={t(
              'price.fields.set_your_rate.fair_tips.strong_chance_title',
              {
                defaultValue: 'Strong chance of landing this'
              }
            )}
            description={t(
              'price.fields.set_your_rate.fair_tips.below_fair_range_confirm',
              {
                defaultValue:
                  "You're well placed to land this one. Note that your quote is below the fair price range for this deal, so you may want to take that into account before confirming."
              }
            )}
          />
        );
      }
      // 达人报价大于等于low && 小于等于high
      if (+newOrderPrice >= low && +newOrderPrice <= high) {
        return (
          <Alert
            type="success"
            title={t(
              'price.fields.set_your_rate.fair_tips.strong_chance_title',
              {
                defaultValue: 'Strong chance of landing this'
              }
            )}
            description={t(
              'price.fields.set_your_rate.fair_tips.within_fair_comfortably_budget_s3',
              {
                defaultValue:
                  "You're within the recommended price range and comfortably inside budget. A great chance of landing this."
              }
            )}
          />
        );
      }
      // 达人报价大于high && 小于等于max
      if (+newOrderPrice > high && +newOrderPrice <= creatorMaxActualGet) {
        return (
          <WarningAlert
            title={t(
              'price.fields.set_your_rate.fair_tips.little_chance_title',
              {
                defaultValue: 'Little chance at this price'
              }
            )}
            description={t(
              'price.fields.set_your_rate.fair_tips.above_fair_range_s3',
              {
                defaultValue:
                  'Your quote is above the recommended price range for this deal. Lowering it to within the recommended price range would give you the best chance of landing the collaboration.'
              }
            )}
            setAsBaseRate={() => setAsBaseRate()}
            setBaseRateLoading={setBaseRateLoading}
            setBaseRateSuccess={setBaseRateSuccess}
            newOrderPrice={newOrderPrice as number | string}
          />
        );
      }
      // 达人报价大于max
      if (+newOrderPrice > creatorMaxActualGet) {
        return (
          <WarningAlert
            title={t(
              'price.fields.set_your_rate.fair_tips.little_chance_title',
              {
                defaultValue: 'Little chance at this price'
              }
            )}
            description={t(
              'price.fields.set_your_rate.fair_tips.above_fair_and_budget_suggest_s3',
              {
                defaultValue:
                  "Your quote is above both the fair price range and this brand's budget of {{budget}}, so it may be harder to land. We'd suggest adjusting it down, to within {{budget}} at least and ideally back toward the fair price range.",
                budget: formattedBudget
              }
            )}
            setAsBaseRate={() => setAsBaseRate()}
            setBaseRateLoading={setBaseRateLoading}
            setBaseRateSuccess={setBaseRateSuccess}
            newOrderPrice={newOrderPrice as number | string}
          />
        );
      }
      return null;
    }

    return null;
  };

  return (
    <>
      {/* {renderBenefitsUnlocked()} */}
      {getTips()}
    </>
  );
}
